import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { recentSearches, searchResults } from '../data/mockData';

const HeroSection: React.FC = () => {
  const [plate, setPlate] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (plate.trim()) {
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        setShowResults(true);
      }, 1500);
    }
  };

  const plateFormatter = (value: string) => {
    // Format like AA-123-AA
    let formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    
    if (formatted.length > 2 && formatted.length <= 5) {
      formatted = `${formatted.slice(0, 2)}-${formatted.slice(2)}`;
    } else if (formatted.length > 5) {
      formatted = `${formatted.slice(0, 2)}-${formatted.slice(2, 5)}-${formatted.slice(5, 7)}`;
    }
    
    return formatted.slice(0, 9); // Limit to AA-123-AA format
  };

  return (
    <motion.section 
      className="relative h-screen flex items-center bg-cover bg-center"
      style={{ 
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url(https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80)' 
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Trouvez les pièces adaptées à votre poids lourd
            <span className="text-[#E60023]"> en un clic</span>
          </motion.h1>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-200 mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Entrez simplement votre immatriculation et accédez aux pièces compatibles avec votre véhicule
          </motion.p>
          
          <motion.div
            className="bg-white rounded-lg shadow-xl p-1"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5, type: 'spring' }}
          >
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row">
              <div className="flex-1 p-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Entrez votre immatriculation (ex: AB-123-CD)"
                    className="block w-full pl-10 pr-3 py-4 text-lg border-0 focus:ring-0 focus:outline-none"
                    value={plate}
                    onChange={(e) => setPlate(plateFormatter(e.target.value))}
                    maxLength={9}
                  />
                </div>
              </div>
              <div className="p-2">
                <motion.button
                  type="submit"
                  className="w-full md:w-auto bg-[#E60023] text-white px-8 py-4 rounded-md font-medium text-lg flex items-center justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isSearching || plate.length < 7}
                >
                  {isSearching ? (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : null}
                  {isSearching ? 'Recherche...' : 'Rechercher'}
                </motion.button>
              </div>
            </form>
          </motion.div>
          
          {!showResults && (
            <motion.div 
              className="mt-4 text-left bg-white/10 backdrop-blur-md rounded-lg p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <p className="text-gray-200 text-sm mb-2">Recherches récentes :</p>
              <div className="flex flex-wrap gap-2">
                {recentSearches.map((search, index) => (
                  <motion.button
                    key={index}
                    className="bg-white/20 hover:bg-white/30 text-white py-1 px-3 rounded text-sm"
                    onClick={() => setPlate(search)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {search}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
          
          {showResults && (
            <motion.div 
              className="mt-8 bg-white rounded-lg shadow-xl overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-gray-100 p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Résultats pour : {plate}</h3>
                  <button 
                    onClick={() => setShowResults(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Information véhicule</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Marque:</span>
                      <p className="font-medium">Renault</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Modèle:</span>
                      <p className="font-medium">T-Series</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Année:</span>
                      <p className="font-medium">2019</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Moteur:</span>
                      <p className="font-medium">13L - 480ch</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">VIN:</span>
                      <p className="font-medium">VF617GPA000012345</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    Pièces disponibles ({searchResults(plate).parts.length})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {searchResults(plate).parts.map((part) => (
                      <motion.div 
                        key={part.id}
                        className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                        whileHover={{ y: -5 }}
                      >
                        <div className="flex flex-row h-24">
                          <div className="w-24 h-24 bg-gray-200">
                            <img src={part.image} alt={part.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="p-3 flex-1">
                            <h5 className="text-sm font-medium truncate">{part.name}</h5>
                            <p className="text-xs text-gray-500">Réf: {part.reference}</p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="font-bold text-[#E60023]">{part.price.toFixed(2)} €</span>
                              <button className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded">
                                Voir détails
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="mt-4 text-center">
                    <motion.button
                      className="bg-[#E60023] text-white px-4 py-2 rounded-md font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Voir toutes les pièces compatibles
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      
      {/* Overlay decorative elements */}
      <div className="absolute left-0 top-1/4 w-64 h-64 bg-[#E60023]/10 rounded-full filter blur-3xl"></div>
      <div className="absolute right-0 bottom-1/4 w-72 h-72 bg-blue-500/10 rounded-full filter blur-3xl"></div>
    </motion.section>
  );
};

export default HeroSection;
